// GateManagement.tsx - Simplified without header and view toggles
import React, { useState, useEffect, useCallback } from 'react';
import { GateForm } from './GateForm';
import { GateList } from './GateList';
import { GateApiService } from './apiService';
import { 
  Gate, 
  Region,
  GateManagementProps 
} from './types';

export const GateManagement: React.FC<GateManagementProps> = ({
  getAccessToken,
  onStatusUpdate
}) => {
  // State management
  const [gates, setGates] = useState<Gate[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingGate, setEditingGate] = useState<Gate | null>(null);
  
  // Region selection and search state
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [gateSearch, setGateSearch] = useState('');
  
  // API service instance
  const [apiService] = useState(() => new GateApiService(getAccessToken));

  // Load regions
  const loadRegions = useCallback(async (searchTerm: string = '') => {
    setIsLoadingRegions(true);
    
    const result = await apiService.loadRegions(searchTerm);
    
    if (result.success && result.data) {
      setRegions(result.data);
    } else {
      onStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to load regions'
      });
    }
    
    setIsLoadingRegions(false);
  }, [apiService, onStatusUpdate]);

  // Load gates for selected region
  const loadGates = useCallback(async (regionId: number, searchTerm: string = '') => {
    setIsLoading(true);
    
    const result = await apiService.loadGates(regionId, searchTerm);
    
    if (result.success && result.data) {
      setGates(result.data);
    } else {
      onStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to load gates'
      });
    }
    
    setIsLoading(false);
  }, [apiService, onStatusUpdate]);

  // Load initial regions
  useEffect(() => {
    loadRegions();
  }, [loadRegions]);

  // Load gates when region or search changes
  useEffect(() => {
    if (selectedRegion) {
      loadGates(selectedRegion.regionId, gateSearch);
    }
  }, [selectedRegion, gateSearch, loadGates]);

  // Handle region selection
  const handleRegionSelect = (region: Region | null) => {
    setSelectedRegion(region);
    if (!region) {
      setGates([]);
    }
  };

  // Handle gate search change
  const handleGateSearchChange = (search: string) => {
    setGateSearch(search);
  };

  // Handle create new gate
  const handleCreateNewGate = () => {
    if (!selectedRegion) {
      onStatusUpdate({ 
        type: 'error', 
        message: 'Please select a region first' 
      });
      return;
    }
    setEditingGate(null);
    setShowForm(true);
  };

  // Handle edit gate
  const handleEditGate = (gate: Gate) => {
    setEditingGate(gate);
    setShowForm(true);
  };

  // Handle back from form
  const handleBackFromGateForm = () => {
    setShowForm(false);
    setEditingGate(null);
  };

  // Handle successful form submission
  const handleGateFormSuccess = () => {
    setShowForm(false);
    setEditingGate(null);
    if (selectedRegion) {
      loadGates(selectedRegion.regionId, gateSearch);
    }
    loadRegions(); // Refresh region counts
  };

  // Handle refresh operations
  const handleRefreshGates = () => {
    if (selectedRegion) {
      loadGates(selectedRegion.regionId, gateSearch);
    }
    loadRegions(); // Also refresh region counts
  };
// Determine which view to render
  const renderContent = () => {
    if (showForm) {
      return (
        <GateForm
          editingGate={editingGate}
          regions={regions}
          selectedRegion={selectedRegion}
          getAccessToken={getAccessToken}
          onStatusUpdate={onStatusUpdate}
          onBack={handleBackFromGateForm}
          onSuccess={handleGateFormSuccess}
        />
      );
    }

    return (
      <GateList
        gates={gates}
        regions={regions}
        isLoading={isLoading}
        isLoadingRegions={isLoadingRegions}
        selectedRegion={selectedRegion}
        gateSearch={gateSearch}
        getAccessToken={getAccessToken}
        onStatusUpdate={onStatusUpdate}
        onRegionSelect={handleRegionSelect}
        onGateSearchChange={handleGateSearchChange}
        onEdit={handleEditGate}
        onCreateNew={handleCreateNewGate}
        onRefresh={handleRefreshGates}
      />
    );
  };

  return (
    <>
      <h1 style={{
        fontSize: '25px',
        fontWeight: 600,
        color: '#1a1a1a',
        margin: '0 0 12px 0',
        padding: '5px 0 0 20px',
        lineHeight: 1.3,
        fontFamily: "'Segoe UI', sans-serif",
        letterSpacing: '-0.01em',
      }}>
        Gate Management
      </h1>

      {renderContent()}
    </>
  );
};